import { Check, Close } from "@mui/icons-material"
import {
  Box,
  CircularProgress,
  Link as MuiLink,
  Skeleton,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material"
// import { Check, XClose } from "@untitled-ui/icons-react"

// import { useTokenInfo } from "../../hooks/use-token-info"
// import { NumberFont } from "../../theme"
// import { formatQuantity, formatRelative } from "../../utils/format"
// import TokenLogo from "../TokenLogo"
// import { TokenPill } from "../TokenPill"
import { Link } from "react-router-dom"

import {
  Activity,
  ActivityAction,
  LimitOrderErrorInfo,
  LimitOrderInfo,
  AgentInfo,
  ProvideOrderErrorInfo,
  ProvideOrderInfo,
  SwapOrderErrorInfo,
  SwapOrderInfo,
} from "@/api//activity-api"
import { IdBlock } from "@/components/IdBlock"
import { NumberFont } from "@/components/RootLayout/fonts"
import { TokenAmountBlock } from "@/components/TokenAmountBlock"
import { useTokenInfo } from "@/hooks/use-token-info"
import { truncateId } from "@/utils/data-utils"
import { formatRelative } from "@/utils/date-utils"
import { formatFullDate, formatTicker } from "@/utils/format"

const TITLE_MAP: Record<ActivityAction, string> = {
  "Order-Confirmation": "Swapped",
  "Order-Error": "Swapped",
  "Burn-Confirmation": "Withdrew",
  "Burn-Error": "Withdrew",
  "Provide-Confirmation": "Provided",
  "Provide-Error": "Provided",
  "Limit-Order-Confirmation": "Swapped",
  "Limit-Order-Error": "Swapped",
  "DCA-Order-Confirmation": "Swapped",
  "DCA-Order-Error": "Swapped",
  "Stop-Order-Confirmation": "Swapped",
  "Stop-Error-Confirmation": "Swapped",
  "Add-Pool-Confirmation": "Created liquidity pool for",
  "Add-Pool-Error": "Created liquidity pool for",
  "Portfolio-Agent-Update": "Portfolio agent",
  "Botega-Agent-Update": "Botega agent",
  "Multi-Hop-Swap-Success-Notice": "Swapped",
  "Multi-Hop-Swap-Start-Notice": "Initiated a multi-hop swap of",
  "Multi-Hop-Swap-Cancel-Notice": "Cancelled a multi-hop swap of",
}

type ActivityRowProps = {
  activity: Activity
}

export function ActivityRow(props: ActivityRowProps) {
  const { activity } = props

  const { agentId, poolId, txId, action, timestamp, errorMessage, orderInfo } = activity

  const filledPercentage = parseFloat((orderInfo as LimitOrderInfo).filledPercentage)
  const incomplete =
    !isNaN(filledPercentage) && filledPercentage > 0 && filledPercentage < 100 && errorMessage

  return (
    <Stack direction="row" gap={2} alignItems="center">
      <Tooltip
        title={
          incomplete
            ? `Partial fill: ${filledPercentage}%`
            : errorMessage
              ? `Error: ${errorMessage}`
              : "Success"
        }
      >
        <Stack
          alignItems="center"
          justifyContent="center"
          sx={{
            flexShrink: 0,
            borderRadius: 1,
            height: 28,
            width: 28,
            background: !errorMessage
              ? "rgba(var(--mui-palette-success-mainChannel) / 0.1)"
              : incomplete
                ? "rgba(var(--mui-palette-warning-mainChannel) / 0.1)"
                : "rgba(var(--mui-palette-error-mainChannel) / 0.1)",
            border: "1px solid",
            borderColor: !errorMessage
              ? "var(--mui-palette-success-main)"
              : "var(--mui-palette-error-main)",
            "& svg": {
              width: "0.85rem",
              height: "0.85rem",
            },
          }}
        >
          {!errorMessage ? (
            <Check style={{ color: "var(--mui-palette-accent-main)" }} />
          ) : incomplete ? (
            <Box
              sx={{ position: "relative", height: "fit-content", transform: "translate(1px, 1px)" }}
            >
              <CircularProgress
                variant="determinate"
                color="warning"
                sx={{
                  opacity: 0.2,
                }}
                size={16}
                thickness={5}
                value={100}
              />
              <CircularProgress
                variant="determinate"
                color="warning"
                value={filledPercentage}
                sx={{
                  position: "absolute",
                  left: 0,
                }}
                size={16}
                thickness={5}
              />
            </Box>
          ) : (
            <Close style={{ color: "var(--mui-palette-error-main)" }} />
          )}
        </Stack>
      </Tooltip>
      <Stack>
        <Stack
          direction="row"
          alignItems="center"
          flexWrap={"wrap"}
          sx={{
            "& > *": {
              marginRight: `8px !important`,
            },
          }}
        >
          <Typography variant="body2">{TITLE_MAP[action]}</Typography>
          {action.includes("Provide") || action.includes("Burn") ? (
            <ProvideActivityDetails orderInfo={orderInfo as ProvideOrderInfo | undefined} />
          ) : action.includes("Limit") ? (
            <LimitOrderActivityDetails orderInfo={orderInfo as LimitOrderInfo | undefined} />
          ) : action.includes("Add-Pool") ? (
            <AddPoolActivityDetails orderInfo={orderInfo as ProvideOrderInfo | undefined} />
          ) : action.includes("Stop") ? (
            <StopLossActivityDetails orderInfo={orderInfo as SwapOrderInfo | undefined} />
          ) : action.includes("Agent-Update") ? (
            <AgentStatusActivity
              orderInfo={orderInfo as AgentInfo | undefined}
              agentId={agentId as string}
            />
          ) : action.includes("Multi-Hop-Swap-Start-Notice") ||
            action.includes("Multi-Hop-Swap-Cancel-Notice") ? (
            <MultiHopStartActivityDetails orderInfo={orderInfo as SwapOrderInfo | undefined} />
          ) : (
            <SwapActivityDetails orderInfo={orderInfo as SwapOrderInfo | undefined} />
          )}
          {action.includes("Burn") && "of liquidity"}
          {action.includes("Provide") && "of liquidity"}
          {action.includes("Stop") && "using a stop order agent"}
          {action.includes("DCA") && "using a dca order agent"}
          {action.includes("Limit") && "using a limit order agent"}
          {!action.includes("Agent-Update") && " on Botega"}
        </Stack>
        <Typography variant="caption" color="secondary">
          <Tooltip title={formatFullDate(new Date(timestamp * 1000))}>
            <span>{formatRelative(new Date(timestamp * 1000))}</span>
          </Tooltip>{" "}
          •{" "}
          {action.includes("Limit") ||
          action.includes("DCA") ||
          action.includes("Stop") ||
          action.includes("Portfolio") ||
          action.includes("Multi-Hop") ? (
            <>
              <MuiLink
                component={Link}
                to={`/entity/${agentId}`}
                color="secondary"
                variant="caption"
              >
                View agent
              </MuiLink>{" "}
              •{" "}
            </>
          ) : (
            <>
              <MuiLink component={Link} to={`/pool/${poolId}`} color="secondary" variant="caption">
                View pool
              </MuiLink>{" "}
              •{" "}
            </>
          )}
          <MuiLink
            href={`https://ao.link/#/message/${txId}`}
            target="_blank"
            rel="noopener noreferrer"
            color="secondary"
            variant="caption"
          >
            View on ao.link
          </MuiLink>
        </Typography>
      </Stack>
    </Stack>
  )
}

function SwapActivityDetails({ orderInfo }: { orderInfo?: SwapOrderInfo | SwapOrderErrorInfo }) {
  const fromTokenId = orderInfo?.fromToken
  const toTokenId = orderInfo?.toToken
  const fromAmount = orderInfo?.fromQuantity
  const toAmount = orderInfo?.toQuantity

  return (
    <>
      {fromAmount === undefined ? (
        <Skeleton width={110} />
      ) : (
        <TokenAmountBlock
          amount={fromAmount}
          tokenId={fromTokenId}
          hideCopyToClipboard
          needsParsing
          showTicker
          color="accent.main"
        />
      )}
      <span>for</span>
      {toAmount === undefined ? (
        <Skeleton width={120} />
      ) : (
        <TokenAmountBlock
          amount={toAmount}
          tokenId={toTokenId}
          hideCopyToClipboard
          needsParsing
          showTicker
          color="accent.main"
        />
      )}
    </>
  )
}

function MultiHopStartActivityDetails({
  orderInfo,
}: {
  orderInfo?: SwapOrderInfo | SwapOrderErrorInfo
}) {
  const fromTokenId = orderInfo?.fromToken
  const toTokenId = orderInfo?.toToken
  const fromAmount = orderInfo?.fromQuantity

  const [toToken] = useTokenInfo(toTokenId)

  return (
    <>
      {fromAmount === undefined ? (
        <Skeleton width={110} />
      ) : (
        <TokenAmountBlock
          amount={fromAmount}
          tokenId={fromTokenId}
          hideCopyToClipboard
          needsParsing
          showTicker
          color="accent.main"
        />
      )}
      <span>for</span>
      <Typography fontFamily={NumberFont} component="span" variant="inherit" color="accent.main">
        <span>{formatTicker(toToken?.ticker, true)}</span>
      </Typography>
    </>
  )
}

function ProvideActivityDetails({
  orderInfo,
}: {
  orderInfo?: ProvideOrderInfo | ProvideOrderErrorInfo
}) {
  const tokenAAmount = orderInfo?.tokenAAmount
  const tokenAId = orderInfo?.tokenA
  const tokenBAmount = orderInfo?.tokenBAmount
  const tokenBId = orderInfo?.tokenB

  return (
    <>
      {tokenBAmount === undefined ? (
        <Skeleton width={120} />
      ) : (
        <TokenAmountBlock
          amount={tokenBAmount}
          tokenId={tokenBId}
          hideCopyToClipboard
          needsParsing
          showTicker
          color="accent.main"
        />
      )}
      <span>and</span>
      {tokenAAmount === undefined ? (
        <Skeleton width={110} />
      ) : (
        <TokenAmountBlock
          amount={tokenAAmount}
          tokenId={tokenAId}
          hideCopyToClipboard
          needsParsing
          showTicker
          color="accent.main"
        />
      )}
    </>
  )
}

function AddPoolActivityDetails({
  orderInfo,
}: {
  orderInfo?: ProvideOrderInfo | ProvideOrderErrorInfo
}) {
  const tokenAId = orderInfo?.tokenA
  const tokenBId = orderInfo?.tokenB

  const [tokenA] = useTokenInfo(tokenAId)
  const [tokenB] = useTokenInfo(tokenBId)

  return (
    <>
      {tokenAId === undefined ? (
        <Skeleton width={120} />
      ) : (
        <Box sx={{ "& a": { color: "accent.main" } }}>
          <IdBlock
            label={tokenA?.ticker || truncateId(tokenAId)}
            value={tokenAId}
            href={`https://ao.link/#/token/${tokenAId}`}
            hideCopyToClipboard
          />
        </Box>
      )}
      <span>and</span>
      {tokenBId === undefined ? (
        <Skeleton width={120} />
      ) : (
        <Box sx={{ "& a": { color: "accent.main" } }}>
          <IdBlock
            label={tokenB?.ticker || truncateId(tokenBId)}
            value={tokenBId}
            href={`https://ao.link/#/token/${tokenBId}`}
            hideCopyToClipboard
          />
        </Box>
      )}
    </>
  )
}

function LimitOrderActivityDetails({
  orderInfo,
}: {
  orderInfo?: LimitOrderInfo | LimitOrderErrorInfo
}) {
  const fromTokenId = orderInfo?.fromToken
  const toTokenId = orderInfo?.toToken
  const fromAmount = orderInfo ? parseFloat(orderInfo.fromQuantity) : undefined
  const toAmount = orderInfo ? parseFloat(orderInfo.toQuantity) : undefined
  const filledPercentage = orderInfo ? parseFloat(orderInfo.filledPercentage) : undefined

  return (
    <>
      {fromAmount === undefined ? (
        <Skeleton width={110} />
      ) : (
        <TokenAmountBlock
          amount={filledPercentage ? (filledPercentage * fromAmount) / 100 : fromAmount}
          // amount={fromAmount}
          tokenId={fromTokenId}
          hideCopyToClipboard
          needsParsing
          showTicker
          color="accent.main"
        />
      )}
      <span>for</span>
      {toAmount === undefined ? (
        <Skeleton width={120} />
      ) : (
        <TokenAmountBlock
          amount={filledPercentage ? (filledPercentage * toAmount) / 100 : toAmount}
          // amount={toAmount}
          tokenId={toTokenId}
          hideCopyToClipboard
          needsParsing
          showTicker
          color="accent.main"
        />
      )}
    </>
  )
}

function StopLossActivityDetails({
  orderInfo,
}: {
  orderInfo?: SwapOrderInfo | SwapOrderErrorInfo
}) {
  const fromTokenId = orderInfo?.fromToken
  const toTokenId = orderInfo?.toToken
  const fromAmount = orderInfo?.fromQuantity
  const toAmount = orderInfo?.toQuantity

  const [toToken] = useTokenInfo(toTokenId)

  return (
    <>
      {fromAmount === undefined ? (
        <Skeleton width={110} />
      ) : (
        <TokenAmountBlock
          amount={fromAmount}
          tokenId={fromTokenId}
          hideCopyToClipboard
          needsParsing
          showTicker
          color="accent.main"
        />
      )}
      <span>for</span>
      {toAmount === undefined ? (
        <>
          <Typography fontFamily={NumberFont} component="span" variant="inherit">
            <span>{toToken?.ticker}</span> <span>(at market price)</span>
          </Typography>
        </>
      ) : (
        <TokenAmountBlock
          amount={toAmount}
          tokenId={toTokenId}
          hideCopyToClipboard
          needsParsing
          showTicker
          color="accent.main"
        />
      )}
    </>
  )
}

function AgentStatusActivity({ orderInfo, agentId }: { orderInfo?: AgentInfo; agentId: string }) {
  const agentName = orderInfo?.agentName
  const status = orderInfo?.status

  return (
    <>
      <Typography fontFamily={NumberFont} component="span" variant="inherit" color="accent.main">
        <span>{agentName || truncateId(agentId)}</span>
      </Typography>
      <span>updated to</span>
      <Typography fontFamily={NumberFont} component="span" variant="inherit" color="accent.main">
        <span>{status}</span>
      </Typography>
    </>
  )
}
